import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import WasteToolbarThumbnail from "./imageCard/WasteToolbarThumbnail";
import { FINES_MATERIAL_KEY } from "../containers/activity/utils/buildMaterialsWithFines";

const CROP_MARGIN_RATIO = 0.08;
const INITIAL_VISIBLE_COUNT = 9;
const LOAD_MORE_STEP = 9;
const GRID_GAP_PX = 16;

const MeasuredObjectThumbnail = ({ object }) => {
  const cellRef = useRef(null);
  // Start at 0 so the Stage only mounts once the cell width is known
  // (avoids a first paint at a guessed 160px then a layout jump).
  const [size, setSize] = useState(0);

  useEffect(() => {
    const node = cellRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width;
      if (width > 0) {
        setSize(Math.floor(width));
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cellRef}
      className="aspect-square w-full rounded-xl overflow-hidden bg-[#f6f6f6] border border-gray-200"
    >
      {size > 0 ? (
        <WasteToolbarThumbnail
          subregion={object.subregion}
          image_url={object.image_url}
          maxWidth={size}
          maxHeight={size}
          marginRatio={CROP_MARGIN_RATIO}
          squareCrop
          fillRectangle
          highlightMode="spotlight"
        />
      ) : null}
    </div>
  );
};

/**
 * "Zoom" panel paired with the material distribution bar chart: 3-column
 * grid of cropped object thumbnails with vertical scroll. Material choice
 * uses the same pill dropdown pattern as characterization (Label : bold value).
 */
export const MaterialZoomPanel = ({
  materials,
  selectedMaterial,
  onSelectMaterial,
  objectsByMaterial,
  isLoading,
}) => {
  const { t } = useTranslation();
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);
  const scrollRef = useRef(null);
  const dropdownRef = useRef(null);

  const updateScrollHint = () => {
    const container = scrollRef.current;
    if (!container) {
      setCanScrollDown(false);
      return;
    }
    setCanScrollDown(
      container.scrollTop + container.clientHeight < container.scrollHeight - 4,
    );
  };

  useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [selectedMaterial]);

  useEffect(() => {
    if (!isDropdownOpen) return undefined;

    const handlePointerDown = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsDropdownOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isDropdownOpen]);

  const selectedEntry = useMemo(
    () => materials.find((material) => material.value === selectedMaterial),
    [materials, selectedMaterial],
  );
  const selectableMaterials = useMemo(
    () => materials.filter((material) => material.value !== FINES_MATERIAL_KEY),
    [materials],
  );
  const objects = objectsByMaterial[selectedMaterial] || [];
  const visibleObjects = objects.slice(0, visibleCount);

  useEffect(() => {
    const frame = requestAnimationFrame(updateScrollHint);
    return () => cancelAnimationFrame(frame);
  }, [visibleObjects.length, selectedMaterial, isLoading]);

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;

    updateScrollHint();
    const isNearEnd =
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - GRID_GAP_PX * 4;
    if (isNearEnd && visibleCount < objects.length) {
      setVisibleCount((current) =>
        Math.min(current + LOAD_MORE_STEP, objects.length),
      );
    }
  };

  const handleScrollDownClick = () => {
    const container = scrollRef.current;
    if (!container) return;
    container.scrollBy({
      top: Math.max(container.clientHeight * 0.75, 120),
      behavior: "smooth",
    });
  };

  return (
    <div className="flex flex-col h-full min-w-0">
      <div className="flex items-center justify-end pb-3 shrink-0 min-w-0">
        <div
          ref={dropdownRef}
          className="relative flex items-center gap-2 border border-[#d3d3d3] rounded-full px-4 bg-white h-10 text-[14px] min-w-0 max-w-full"
        >
          <span className="text-[#222222] whitespace-nowrap select-none font-medium shrink-0">
            {t("Waste type")}
          </span>
          <span className="text-[#5e5e5e] select-none shrink-0">:</span>
          <button
            type="button"
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
            onClick={() => setIsDropdownOpen((open) => !open)}
            className="flex items-center gap-2 min-w-0 flex-1 bg-transparent border-0 p-0 cursor-pointer text-left"
          >
            <span className="truncate font-bold text-[#222222]">
              {selectedEntry?.label ?? t("Detected material")}
            </span>
            <i
              className={`fas fa-chevron-${isDropdownOpen ? "up" : "down"} text-xs text-gray-400 shrink-0`}
              aria-hidden
            />
          </button>

          {isDropdownOpen ? (
            <div
              className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto min-w-full"
              role="listbox"
            >
              <div className="py-1">
                {selectableMaterials.map((material) => {
                  const isSelected = material.value === selectedMaterial;
                  return (
                    <button
                      key={material.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => {
                        onSelectMaterial(material.value);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm cursor-pointer transition-colors ${
                        isSelected
                          ? "bg-gray-50 hover:bg-[#00A7A6] hover:text-white"
                          : "hover:bg-[#00A7A6] hover:text-white"
                      }`}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: material.color }}
                      />
                      <span className="flex-1 truncate font-medium">
                        {material.label}
                      </span>
                      <span className="text-xs opacity-70 shrink-0">
                        ({material.count})
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="relative flex-1 min-h-0">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="h-full overflow-y-auto overflow-x-hidden"
        >
          {isLoading ? (
            <div className="flex items-center justify-center h-full text-sm text-[#9CA3AF]">
              {t("Loading detected objects")}…
            </div>
          ) : objects.length === 0 ? (
            <div className="flex items-center justify-center h-full text-sm text-[#9CA3AF] text-center px-4">
              {t("No detected objects for this material")}
            </div>
          ) : (
            <div key={selectedMaterial} className="grid grid-cols-3 gap-4">
              {visibleObjects.map((object, index) => (
                <MeasuredObjectThumbnail
                  key={`${selectedMaterial}-${object.image_url}-${object.subregion.id || index}`}
                  object={object}
                />
              ))}
            </div>
          )}
        </div>

        {canScrollDown ? (
          <button
            type="button"
            onClick={handleScrollDownClick}
            aria-label="Scroll down"
            className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 inline-flex items-center justify-center w-9 h-9 p-0 rounded-full bg-white border border-gray-200 shadow-sm text-[#5E5E5E] hover:text-[#222222] hover:border-gray-300 transition-colors cursor-pointer"
          >
            <i
              className="fas fa-chevron-down text-[11px] leading-none relative top-px"
              aria-hidden
            />
          </button>
        ) : null}
      </div>
    </div>
  );
};
